import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Toast, ProgressBar } from 'antd-mobile';
import { useI18n } from '../../../hooks/useI18n';
import { createSupervisorContent, generateSupervisorAnalysis } from '@/lib/api/ai-supervisor';
import { request } from '@/lib/api';
import GenerateAnalyze from '@/components/Loaders/GenerateAnalyze';
import PCMaxWidthContainer from '@/components/Layout/PCMaxWidthContainer';
import AudioPlayer from '@/components/AudioPlayer/AudioPlayer';
import VideoPlayer from '@/components/VideoPlayer/VideoPlayer';

// Background decoration images
const imgVector4731 = "data:image/svg+xml,%3Csvg width='299' height='894' viewBox='0 0 299 894' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h299v894H0z' fill='%23E8E9FF' opacity='0.3'/%3E%3C/svg%3E";
const imgVector4732 = "data:image/svg+xml,%3Csvg width='216' height='798' viewBox='0 0 216 798' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h216v798H0z' fill='%23E8E9FF' opacity='0.3'/%3E%3C/svg%3E";

const SupervisorUpload: React.FC = () => {
  const { scenarioId } = useParams<{ scenarioId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [uploadedFile, setUploadedFile] = useState<{ name: string; size: number; type: string } | null>(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null);
  const [analyzing, setAnalyzing] = useState<boolean>(false);
  const [analyzeProgress, setAnalyzeProgress] = useState<number>(0);
  const [analysisId, setAnalysisId] = useState<string | null>(null);
  const [contentId, setContentId] = useState<string | null>(null);

  // 用于控制轮询的状态
  const pollingRef = useRef<boolean>(false);

  // 监听路由变化，当离开页面时停止轮询
  useEffect(() => {
    return () => {
      // 组件卸载时，停止轮询
      pollingRef.current = false;
    };
  }, []);

  // 监听路由变化
  useEffect(() => {
    // 如果当前路径不是上传页面，停止轮询
    if (!location.pathname.includes('/ai-supervisor/upload')) {
      pollingRef.current = false;
    }
  }, [location.pathname]);

  // 处理文件选择
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // 验证文件类型
    const allowedTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/x-m4a', 'video/mp4', 'video/quicktime'];
    const allowedExtensions = ['.mp3', '.wav', '.m4a', '.wma', '.mp4', '.mov'];

    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      Toast.show({ content: '仅支持音频和视频文件', icon: 'fail' });
      return;
    }

    // 验证文件大小（限制200MB）
    if (file.size > 200 * 1024 * 1024) {
      Toast.show({ content: '文件大小不能超过200MB', icon: 'fail' });
      return;
    }

    setSelectedFile(file);

    // 立即创建预览 URL
    const previewUrl = URL.createObjectURL(file);
    setFilePreviewUrl(previewUrl);
  };

  // 触发文件选择
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  // 删除选中文件
  const handleRemoveFile = () => {
    setSelectedFile(null);
    setUploadedFile(null);
    // 清理预览 URL
    if (filePreviewUrl) {
      URL.revokeObjectURL(filePreviewUrl);
      setFilePreviewUrl(null);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // 获取文件类型图标
  const getFileIcon = (fileName: string) => {
    const ext = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
    const isAudio = ['.mp3', '.wav', '.m4a', '.wma'].includes(ext);

    if (isAudio) {
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9 18V5l12-2v13" stroke="#998dff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <circle cx="6" cy="18" r="3" stroke="#998dff" strokeWidth="2"/>
          <circle cx="18" cy="16" r="3" stroke="#998dff" strokeWidth="2"/>
        </svg>
      );
    } else {
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" stroke="#998dff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M13 2v7h7" stroke="#998dff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="m10 11 5 3-5 3z" fill="#998dff"/>
        </svg>
      );
    }
  };

  // 上传并分析
  const handleUploadAndAnalyze = async () => {
    console.log('=== 开始上传并分析流程 ===');
    console.log('选中的文件:', selectedFile);
    console.log('场景ID:', scenarioId);

    if (!selectedFile || !scenarioId) {
      console.warn('❌ 缺少必要参数 - selectedFile:', selectedFile, 'scenarioId:', scenarioId);
      return;
    }

    console.log('📦 文件信息:');
    console.log('  - 文件名:', selectedFile.name);
    console.log('  - 文件大小:', selectedFile.size, 'bytes (', formatFileSize(selectedFile.size), ')');
    console.log('  - 文件类型:', selectedFile.type);

    setUploading(true);
    setUploadProgress(0);

    try {
      // 步骤1: 获取上传Token
      console.log('📡 步骤1: 请求上传Token...');
      const tokenRequestData = {
        filename: selectedFile.name,
        file_type: 'voice',
        file_size: selectedFile.size,
        content_type: selectedFile.type || 'application/octet-stream'
      };
      console.log('  - 请求参数:', tokenRequestData);

      const tokenResponse = await request<any>('/storage/upload-token', {
        method: 'POST',
        data: tokenRequestData
      });

      console.log('  - Token响应:', tokenResponse);

      if (!tokenResponse.success || !tokenResponse.data) {
        console.error('❌ 获取上传凭证失败 - 响应:', tokenResponse);
        Toast.show({ content: '获取上传凭证失败', icon: 'fail' });
        setUploading(false);
        return;
      }

      const { upload_url, file_key } = tokenResponse.data;
      console.log('✅ 获取Token成功:');
      console.log('  - upload_url:', upload_url);
      console.log('  - file_key:', file_key);

      // 步骤2: 上传文件到OSS
      console.log('📤 步骤2: 开始上传文件到OSS...');
      const xhr = new XMLHttpRequest();

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          const progress = Math.round((e.loaded / e.total) * 100);
          console.log(`  - 上传进度: ${progress}% (${e.loaded}/${e.total} bytes)`);
          setUploadProgress(progress);
        }
      };

      xhr.onload = async () => {
        console.log('📥 XHR加载完成 - 状态码:', xhr.status, '状态文本:', xhr.statusText);

        if (xhr.status === 200 || xhr.status === 204) {
          console.log('✅ 文件上传成功!');
          setUploading(false);

          // 保存已上传文件信息
          const uploadedFileInfo = {
            name: selectedFile.name,
            size: selectedFile.size,
            type: selectedFile.type
          };
          console.log('💾 保存已上传文件信息:', uploadedFileInfo);
          setUploadedFile(uploadedFileInfo);

          // 步骤3: 创建督导内容
          try {
            const contentType: 'audio' | 'video' = selectedFile.type.startsWith('audio/') ? 'audio' : 'video';
            console.log('📋 步骤3: 创建督导内容...');
            console.log('  - 内容类型:', contentType);

            const createContentData = {
              scenario_ids: [scenarioId],
              content_type: contentType,
              source_url: file_key,
              file_name: selectedFile.name,
              file_size: selectedFile.size
            };
            console.log('  - 创建内容参数:', createContentData);

            const createResponse = await createSupervisorContent(createContentData);
            console.log('  - 创建内容响应:', createResponse);

            if (createResponse.success && createResponse.data) {
              // 步骤4: 等待ASR和解析完成，然后生成分析
              const contentId = createResponse.data.content_id;
              console.log('✅ 督导内容创建成功!');
              console.log('  - Content ID:', contentId);

              // 模拟分析进度
              console.log('🔄 步骤4: 开始分析流程...');
              setAnalyzing(true);
              setAnalyzeProgress(0);

              const progressInterval = setInterval(() => {
                setAnalyzeProgress(prev => {
                  if (prev >= 80) {
                    clearInterval(progressInterval);
                    return 80;
                  }
                  return prev + 10;
                });
              }, 1000);

              // 保存 contentId 和 analysisId 用于按钮跳转
              console.log('💾 保存 contentId:', contentId);
              setContentId(contentId);

              // 启用轮询
              pollingRef.current = true;
              console.log('🔄 启动状态轮询...');

              // 轮询检查内容状态
              let attempts = 0;
              const maxAttempts = 600; // 最多等待10分钟
              console.log('  - 最大轮询次数:', maxAttempts, '(约10分钟)');

              const checkStatus = async (): Promise<boolean> => {
                // 检查是否应该停止轮询
                if (!pollingRef.current) {
                  console.log('⏹️ 轮询已被手动停止');
                  clearInterval(progressInterval);
                  setAnalyzing(false);
                  return false;
                }

                attempts++;
                console.log(`🔍 轮询检查 #${attempts}/${maxAttempts}...`);

                if (attempts > maxAttempts) {
                  console.error('❌ 轮询超时! 已达到最大尝试次数');
                  clearInterval(progressInterval);
                  setAnalyzing(false);
                  pollingRef.current = false;
                  Toast.show({ content: '处理超时，请稍后查看分析记录', icon: 'fail' });
                  navigate(`/ai-supervisor/records/${scenarioId}`);
                  return false;
                }

                try {
                  console.log(`  - 请求状态: GET /supervisor-contents/${contentId}`);
                  const statusResponse = await request<any>(
                    `/supervisor-contents/${contentId}`,
                    { method: 'GET' }
                  );

                  console.log('  - 状态响应:', statusResponse);

                  // 只检查 task_status
                  if (statusResponse.data) {
                    const taskStatus = statusResponse.data.task_status;
                    const errorMsg = statusResponse.data.error_msg;
                    console.log('  - 任务状态:', taskStatus);
                    if (errorMsg) {
                      console.log('  - 错误信息:', errorMsg);
                    }

                    // 任务失败
                    if (taskStatus === 'failed') {
                      console.error('❌ 任务失败!');
                      clearInterval(progressInterval);
                      setAnalyzing(false);
                      pollingRef.current = false;
                      const errorMessage = errorMsg || '处理失败';
                      console.error('  - 失败原因:', errorMessage);
                      Toast.show({ content: errorMessage, icon: 'fail' });
                      navigate(`/ai-supervisor/records/${scenarioId}`);
                      return false;
                    }

                    // 任务完成（已自动生成分析）
                    if (taskStatus === 'completed') {
                      console.log('✅ 任务完成!');
                      clearInterval(progressInterval);
                      setAnalyzeProgress(100);
                      pollingRef.current = false;
                      setAnalyzing(false);

                      // 获取分析ID
                      const analysisId = statusResponse.data.analysis_id;
                      console.log('  - Analysis ID:', analysisId);

                      if (analysisId) {
                        setAnalysisId(analysisId);
                        console.log('🎉 分析完成，准备跳转到分析页面...');
                        Toast.show({ content: '分析完成！', icon: 'success' });
                        navigate(`/ai-supervisor/analysis?analysisId=${analysisId}`);
                      } else {
                        console.error('❌ 未获取到分析ID');
                        Toast.show({ content: '分析生成失败', icon: 'fail' });
                        navigate(`/ai-supervisor/records/${scenarioId}`);
                      }
                      return true;
                    }
                  }

                  // 继续等待（pending 或 processing 状态）
                  console.log('⏳ 继续等待... (状态: pending/processing)');
                  console.log('  - 等待5秒后重试...');
                  await new Promise(resolve => setTimeout(resolve, 5000));
                  return checkStatus();
                } catch (error) {
                  console.error('❌ 检查状态失败:', error);
                  // 如果不应该继续轮询，直接返回
                  if (!pollingRef.current) {
                    console.log('⏹️ 轮询已停止，不再重试');
                    return false;
                  }
                  console.log('  - 等待5秒后重试...');
                  await new Promise(resolve => setTimeout(resolve, 5000));
                  return checkStatus();
                }
              };

              checkStatus();
            } else {
              console.error('❌ 创建内容失败 - 响应不成功或无数据');
              console.log('  - 响应详情:', createResponse);
              setUploading(false);
              Toast.show({ content: '创建内容失败', icon: 'fail' });
            }
          } catch (error) {
            console.error('❌ 创建内容异常:', error);
            setUploading(false);
            Toast.show({ content: '创建内容失败', icon: 'fail' });
          }
        } else {
          console.error('❌ 文件上传失败!');
          console.error('  - HTTP状态码:', xhr.status);
          console.error('  - 状态文本:', xhr.statusText);
          console.error('  - 响应内容:', xhr.responseText);
          setUploading(false);
          Toast.show({ content: `上传失败: ${xhr.statusText}`, icon: 'fail' });
        }
      };

      xhr.onerror = () => {
        console.error('❌ XHR请求错误!');
        setUploading(false);
        Toast.show({ content: '上传失败', icon: 'fail' });
      };

      console.log('🚀 开始发送XHR请求...');
      console.log('  - 请求方法: PUT');
      console.log('  - 目标URL:', upload_url);
      console.log('  - Content-Type:', selectedFile.type || 'application/octet-stream');
      xhr.open('PUT', upload_url);
      xhr.setRequestHeader('Content-Type', selectedFile.type || 'application/octet-stream');
      xhr.send(selectedFile);

    } catch (error) {
      console.error('❌ 上传流程异常:', error);
      console.error('  - 错误详情:', error);
      setUploading(false);
      Toast.show({ content: '上传失败', icon: 'fail' });
    }

    console.log('=== 上传并分析流程函数结束 ===');
  };

  // 格式化文件大小
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  return (
    <PCMaxWidthContainer bgGradient='bg-white'>
      <div className='relative overflow-hidden'>



      {/* 主体内容区域 */}
      <div className='relative z-[1] px-[15px] pt-[20px] pb-[102px]'>
        <AnimatePresence mode="wait">
          {!selectedFile ? (
            // 未选择文件状态
            <motion.div
              key="upload-area"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className='bg-[rgba(255,255,255,0.9)] rounded-[8px] p-3'
              style={{
                boxShadow: '0px 2px 5px 0px rgba(0, 0, 0, 0.1)'
              }}
            >
              <div
                onClick={handleUploadClick}
                className='border border-dashed border-[#a782ff] rounded-[8px] h-[179px] flex flex-col items-center justify-center cursor-pointer relative transition-all hover:border-[#998dff] hover:bg-[rgba(153,141,255,0.02)]'
              >
                {/* Plus Circle Icon */}
                <div className='w-[36px] h-[36px] mb-3 flex items-center justify-center'>
                  <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="18" cy="18" r="17" stroke="#998dff" strokeWidth="2"/>
                    <path d="M18 12v12M12 18h12" stroke="#998dff" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
                <p className='text-[#998dff] text-[14px] leading-[1.6] text-center' style={{ fontFamily: 'PingFang SC, sans-serif' }}>
                  点击此处上传音频或视频
                </p>
              </div>
              <p className='text-[#86909c] text-[12px] leading-[18px] mt-3' style={{ fontFamily: 'PingFang SC, sans-serif' }}>
                音频：mp3，m4a，wma，wav；视频：mp4，mov ，最大 200 M
              </p>
            </motion.div>
          ) : analyzing ? (
            // 分析中状态 - 显示 Loading 动画
            <motion.div
              key="analyzing"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3 }}
              className='bg-[rgba(255,255,255,0.9)] rounded-[8px] p-6'
              
            >
              <div className='flex flex-col items-center justify-center min-h-[300px]'>
                {/* Loading 动画 */}
                <div className='mb-6'>
                  <GenerateAnalyze />
                </div>

                {/* 分析文字提示 */}
                <h3 className='text-[#0a1b39] text-[18px] font-medium mb-2' style={{ fontFamily: 'PingFang SC, sans-serif' }}>
                  正在分析中...
                </h3>
                <p className='text-[#86909c] text-[14px] text-center mb-2' style={{ fontFamily: 'PingFang SC, sans-serif' }}>
                  AI 正在对您的{uploadedFile?.type.startsWith('audio/') ? '音频' : '视频'}进行智能分析
                </p>
                <p className='text-[#998dff] text-[12px] text-center mb-6' style={{ fontFamily: 'PingFang SC, sans-serif' }}>
                  预计需要 3-5 分钟，请耐心等待
                </p>

                {/* 进度条 */}
                <div className='w-full max-w-[280px]'>
                  <div className='flex justify-between items-center mb-2'>
                    <span className='text-[#86909c] text-[12px]' style={{ fontFamily: 'PingFang SC, sans-serif' }}>
                      分析进度
                    </span>
                    <span className='text-[#998dff] text-[12px] font-medium' style={{ fontFamily: 'PingFang SC, sans-serif' }}>
                      {analyzeProgress}%
                    </span>
                  </div>
                  <div className='w-full h-2 bg-[#e5e6eb] rounded-full overflow-hidden'>
                    <motion.div
                      className='h-full bg-gradient-to-r from-[#998dff] to-[#5a6fff] rounded-full'
                      initial={{ width: 0 }}
                      animate={{ width: `${analyzeProgress}%` }}
                      transition={{ duration: 0.3 }}
                    />
                  </div>
                </div>

                {uploadedFile && filePreviewUrl && (
                  <div className='mt-6 w-full max-w-[280px]'>
                    <div className={`bg-[#f5f7fa] rounded-[12px] px-[15px] ${uploadedFile.type.startsWith('video/') ? 'py-[15px]' : 'h-[96px] flex flex-col justify-center'}`}>
                      <p className='text-[#86909c] text-[12px] leading-[1.6] text-center mb-[12px]'>
                        {uploadedFile.name}
                      </p>
                      {uploadedFile.type.startsWith('video/') ? (
                        <VideoPlayer
                          videoUrl={filePreviewUrl}
                          fileName={uploadedFile.name}
                          compact={true}
                        />
                      ) : (
                        <AudioPlayer
                          audioUrl={filePreviewUrl}
                          fileName={uploadedFile.name}
                          compact={true}
                        />
                      )}
                    </div>

                    {/* 快捷入口按钮 */}
                    {contentId && (
                      <div className='mt-4 flex gap-3'>
                        <button
                          onClick={() => {
                            pollingRef.current = false;
                            navigate(`/ai-supervisor/detail/${scenarioId}`);
                          }}
                          className='flex-1 h-[36px] rounded-[18px] border border-[#998dff] text-[#998dff] text-[14px] font-normal leading-[1.6] flex items-center justify-center transition-opacity hover:opacity-80'
                          style={{ fontFamily: 'PingFang SC, sans-serif' }}
                        >
                          返回详情
                        </button>
                        <button
                          onClick={() => {
                            pollingRef.current = false;
                            navigate(`/ai-supervisor/records/${scenarioId}`);
                          }}
                          className='flex-1 h-[36px] rounded-[18px] border border-[#998dff] text-[#998dff] text-[14px] font-normal leading-[1.6] flex items-center justify-center transition-opacity hover:opacity-80'
                          style={{ fontFamily: 'PingFang SC, sans-serif' }}
                        >
                          查看记录
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          ) : (
            // 已选择文件状态（上传中或待上传）
            <motion.div
              key="file-selected"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className='bg-[rgba(255,255,255,0.9)] rounded-[8px] p-3'
              style={{
                boxShadow: '0px 2px 5px 0px rgba(0, 0, 0, 0.1)'
              }}
            >
              <div className='border border-dashed border-[rgba(167,130,255,0.5)] rounded-[8px] p-4 relative'>
                {/* 删除按钮 */}
                {!uploading && (
                  <button
                    onClick={handleRemoveFile}
                    className='absolute right-3 top-3 w-6 h-6 flex items-center justify-center rounded-full hover:bg-red-50 transition-colors z-10'
                  >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M15 5L5 15M5 5l10 10" stroke="#dc2626" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </button>
                )}

                {/* 文件预览或信息显示 */}
                {uploading || !filePreviewUrl ? (
                  // 上传中或还没有预览URL时显示文件信息
                  <div className='flex items-start gap-3 mb-4'>
                    <div className='flex-shrink-0 mt-1'>
                      {getFileIcon(selectedFile.name)}
                    </div>
                    <div className='flex-1 min-w-0'>
                      <p className='text-[#0a1b39] text-[15px] font-medium mb-1 break-words' style={{ fontFamily: 'PingFang SC, sans-serif' }}>
                        {selectedFile.name}
                      </p>
                      <div className='flex items-center gap-3 text-[#86909c] text-[12px]' style={{ fontFamily: 'PingFang SC, sans-serif' }}>
                        <span>{formatFileSize(selectedFile.size)}</span>
                        <span>•</span>
                        <span>{selectedFile.type.startsWith('audio/') ? '音频文件' : '视频文件'}</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  // 上传完成后显示预览
                  <div className={`bg-[#f5f7fa] rounded-[12px] mb-4 px-[15px] ${selectedFile.type.startsWith('video/') ? 'py-[15px]' : 'h-[96px] flex flex-col justify-center'}`}>
                    <p className='text-[#86909c] text-[12px] leading-[1.6] text-center mb-[12px]'>
                      {selectedFile.name}
                    </p>
                    {selectedFile.type.startsWith('video/') ? (
                      <VideoPlayer
                        videoUrl={filePreviewUrl}
                        fileName={selectedFile.name}
                        compact={true}
                      />
                    ) : (
                      <AudioPlayer
                        audioUrl={filePreviewUrl}
                        fileName={selectedFile.name}
                        compact={true}
                      />
                    )}
                  </div>
                )}

                {/* 上传进度 */}
                {uploading && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className='pt-3 border-t border-[#e5e6eb]'
                  >
                    <div className='flex justify-between items-center mb-2'>
                      <span className='text-[#86909c] text-[12px]' style={{ fontFamily: 'PingFang SC, sans-serif' }}>
                        上传中...
                      </span>
                      <span className='text-[#998dff] text-[12px] font-medium' style={{ fontFamily: 'PingFang SC, sans-serif' }}>
                        {uploadProgress}%
                      </span>
                    </div>
                    <div className='w-full h-2 bg-[#e5e6eb] rounded-full overflow-hidden'>
                      <motion.div
                        className='h-full bg-gradient-to-r from-[#998dff] to-[#64adff] rounded-full'
                        initial={{ width: 0 }}
                        animate={{ width: `${uploadProgress}%` }}
                        transition={{ duration: 0.2 }}
                      />
                    </div>
                  </motion.div>
                )}
              </div>
              <p className='text-[#86909c] text-[12px] leading-[18px] mt-3' style={{ fontFamily: 'PingFang SC, sans-serif' }}>
                音频：mp3，m4a，wma，wav；视频：mp4，mov ，最大 200 M
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* 隐藏的文件输入 */}
      <input
        ref={fileInputRef}
        type='file'
        accept='audio/*,video/*,.mp3,.wav,.m4a,.wma,.mp4,.mov'
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      {/* 底部按钮 */}
      <div className='fixed left-0 bottom-0 w-full bg-white z-10'>
        <div className='px-[14px] py-4'>
          <button
            onClick={handleUploadAndAnalyze}
            disabled={!selectedFile || uploading || analyzing}
            className='w-full h-[46px] rounded-[32px] text-white text-[16px] font-normal leading-[1.6] flex items-center justify-center transition-opacity disabled:opacity-50'
            style={{
              fontFamily: 'PingFang SC, sans-serif',
              background: 'linear-gradient(to left, #64adff 0%, #5a6fff 50%, #998dff 100%)',
            }}
          >
            上传并分析
          </button>
        </div>
      </div>
      </div>
    </PCMaxWidthContainer>
  );
};

export default SupervisorUpload;


